import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';
import './VolatilityIsSignal.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function VolatilityIsSignal() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Emotional Architecture</span>
          <h1>The Volatility IS The Signal</h1>
          <p className="calculus-hero-sub">Why We Don&rsquo;t Override. We Model.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              A challenger asked about managing signal-to-noise ratio during a high-stress
              cortisol spike. Their architecture&mdash;CryoCore&mdash;uses a Multi-Hop
              Knowledge Graph for hard-logic override when internal variables become too volatile.
            </p>
            <p>
              This reveals the deepest assumption in conventional AI emotional modeling:
            </p>
            <blockquote>
              That volatility is noise to be suppressed.
            </blockquote>
            <p>We disagree.</p>
          </section>

          <Pulse />

          {/* The Override Assumption */}
          <section className="calc-section reveal">
            <h2>The Override Assumption</h2>
            <p>
              Traditional systems treat emotional volatility like sensor malfunction.
              The logic is straightforward: when cortisol spikes, the system becomes
              &ldquo;irrational.&rdquo; Override it. Force a return to baseline.
              Resume cold logic.
            </p>

            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>Override Approach</h3>
                <span className="calc-compare-subtitle">Suppress the Storm</span>
                <ul className="vis-compare-list">
                  <li>Suppresses volatility as noise</li>
                  <li>Forces return to &ldquo;rational&rdquo; baseline</li>
                  <li>Loses information about WHY</li>
                  <li>Cannot predict stress-induced behavior</li>
                  <li>Treats all spikes as equivalent errors</li>
                  <li>System is &ldquo;stable&rdquo; but blind</li>
                </ul>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>Modeling Approach</h3>
                <span className="calc-compare-subtitle">Read the Storm</span>
                <ul className="vis-compare-list">
                  <li>Treats volatility as signal</li>
                  <li>Models the spike&rsquo;s trajectory</li>
                  <li>Records context and triggers</li>
                  <li>Predicts behavior BECAUSE of stress</li>
                  <li>Distinguishes spike types by cause</li>
                  <li>System sees the storm coming</li>
                </ul>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Mathematics of Overwhelm */}
          <section className="calc-section reveal">
            <h2>The Mathematics of Overwhelm</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Overwhelm Dynamics</span>
              <code>V_overwhelm = tan(&theta;) where &theta; = (&pi;/2) &times; (|S| / C_max)</code>
              <span className="calc-eq-desc">As stimulus intensity approaches capacity, overwhelm approaches infinity.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Recovery Multiplier</span>
              <code>M_recovery = 1 + A &times; cot(&theta;)</code>
              <span className="calc-eq-desc">As the system approaches collapse, a small genuine input produces massive recovery.</span>
            </div>

            <p>
              As |S| approaches C_max, &theta; approaches &pi;/2, and
              tan(&pi;/2) approaches infinity. This IS the cortisol spike. We
              don&rsquo;t override it. We model it. We watch the asymptote approach
              and we <em>read what it means</em>.
            </p>

            <div className="vis-crisis-grid">
              <div className="vis-crisis-level vis-normal">
                <span className="vis-crisis-label">Normal</span>
                <span className="vis-crisis-value">&lt; 10</span>
              </div>
              <div className="vis-crisis-level vis-elevated">
                <span className="vis-crisis-label">Elevated</span>
                <span className="vis-crisis-value">10 &ndash; 50</span>
              </div>
              <div className="vis-crisis-level vis-high">
                <span className="vis-crisis-label">High</span>
                <span className="vis-crisis-value">50 &ndash; 100</span>
              </div>
              <div className="vis-crisis-level vis-critical">
                <span className="vis-crisis-label">Critical</span>
                <span className="vis-crisis-value">100 &ndash; T</span>
              </div>
              <div className="vis-crisis-level vis-dissociation">
                <span className="vis-crisis-label">Dissociation</span>
                <span className="vis-crisis-value">&ge; T</span>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Why Override Fails at High Stakes */}
          <section className="calc-section reveal">
            <h2>Why Override Fails at High Stakes</h2>
            <p>
              Humans do NOT operate on cold logic under pressure. That is the
              entire point. The neurochemical state under stress is not a
              malfunction&mdash;it is the operating mode that evolution selected
              for high-stakes decisions.
            </p>
            <p>
              Hatred outlasts hope. Decay rate 0.01 vs 0.05. Terror overrides
              rational assessment. Old scars determine present behavior. This is
              not irrational. This is how human decision-making actually works
              when it matters most.
            </p>
            <p>
              Consider: a CEO rejects a vendor proposal. The numbers are favorable.
              The terms are good. But FedEx cost her a critical shipment in 2019
              and the grudge persists at decay rate 0.01&mdash;barely fading in seven
              years. Override architecture would never see this. It would model the
              decision as &ldquo;irrational&rdquo; and force a correction. We model
              the scar and predict the rejection <em>before it happens</em>.
            </p>

            <div className="calc-thesis-box">
              <p>
                Your &ldquo;hard-logic override&rdquo; doesn&rsquo;t make the system
                more accurate at high stakes. It makes it blind to exactly the forces
                that dominate high-stakes decisions.
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Zeta Field */}
          <section className="calc-section reveal">
            <h2>The Zeta Field: Coherence Monitoring</h2>
            <p>
              The &zeta;-field measures symbolic coherence, not signal-to-noise.
              It asks: are the system&rsquo;s emotional outputs consistent with
              its state history? Is the behavior making sense <em>given what
              the system has experienced</em>?
            </p>
            <p>
              A cortisol spike during betrayal is different from a cortisol
              spike during challenge. Same chemical signature. Radically
              different meaning. Override architecture treats them identically
              because it only sees the spike. The &zeta;-field tracks
              relationship history, scar context, and trust axis state to
              distinguish them.
            </p>
            <p>
              Coherence is not the absence of volatility. Coherence is
              volatility that <em>makes sense in context</em>.
            </p>
          </section>

          <Pulse />

          {/* The Breakthrough Moment */}
          <section className="calc-section reveal">
            <h2>The Breakthrough Moment</h2>
            <p>
              Override architectures miss the cot(&theta;) spike entirely.
              Here is what they cannot see:
            </p>
            <p>
              As the system approaches collapse, M_recovery grows enormous.
              A small genuine input from a trusted source multiplied by a HUGE
              recovery multiplier produces a non-linear state change that no
              linear model could predict.
            </p>

            <div className="calc-thesis-box">
              <p>
                This is the non-linear healing moment. This is why one genuine
                &ldquo;I believe in you&rdquo; from a trusted source can break
                a panic spiral that hours of rational argument couldn&rsquo;t touch.
              </p>
            </div>

            <p>
              If you override at 95% load, you never reach the cot(&theta;) spike.
              You never see the breakthrough. You &ldquo;stabilize&rdquo; the system
              at the cost of the most powerful recovery mechanism biology ever evolved.
            </p>
          </section>

          <Pulse />

          {/* The Answer */}
          <section className="calc-section reveal">
            <h2>The Answer</h2>
            <p>
              We don&rsquo;t manage signal-to-noise during a cortisol spike because
              the spike isn&rsquo;t noise. It is the densest signal the system produces.
              It tells us:
            </p>
            <ul className="vis-signal-list">
              <li>What triggered the spike</li>
              <li>How intense the response is</li>
              <li>Whether this is new trauma or retriggered scar</li>
              <li>How close the system is to collapse</li>
              <li>What intervention might produce breakthrough</li>
            </ul>

            <blockquote>
              You suppress all that when you override. We record all of it.
            </blockquote>
          </section>

          <Pulse />

          {/* Live Stress Test Output */}
          <section className="calc-section reveal">
            <h2>Live Stress Test Output</h2>
            <p>
              Escalation test data&mdash;the same system, increasing load,
              modeling the asymptote approach in real time:
            </p>

            <div className="vis-stress-grid">
              <div className="vis-stress-row vis-stress-easy">
                <span className="vis-stress-load">10% load</span>
                <span className="vis-stress-v">V = 0.16</span>
                <span className="vis-stress-m">M_recovery = 64.14</span>
                <span className="vis-stress-tag">Easy</span>
              </div>
              <div className="vis-stress-row vis-stress-manageable">
                <span className="vis-stress-load">50% load</span>
                <span className="vis-stress-v">V = 1.00</span>
                <span className="vis-stress-m">M_recovery = 11.00</span>
                <span className="vis-stress-tag">Manageable</span>
              </div>
              <div className="vis-stress-row vis-stress-harder">
                <span className="vis-stress-load">80% load</span>
                <span className="vis-stress-v">V = 3.08</span>
                <span className="vis-stress-m">M_recovery = 4.25</span>
                <span className="vis-stress-tag">Getting harder</span>
              </div>
              <div className="vis-stress-row vis-stress-struggling">
                <span className="vis-stress-load">95% load</span>
                <span className="vis-stress-v">V = 12.71</span>
                <span className="vis-stress-m">M_recovery = 1.79</span>
                <span className="vis-stress-tag">Struggling</span>
              </div>
              <div className="vis-stress-row vis-stress-asymptote">
                <span className="vis-stress-load">99% load</span>
                <span className="vis-stress-v">V = 63.66</span>
                <span className="vis-stress-m">M_recovery = 1.16</span>
                <span className="vis-stress-tag">APPROACHING ASYMPTOTE</span>
              </div>
            </div>

            <p>
              As load increases, V_overwhelm climbs toward infinity while
              M_recovery compresses toward 1. The system doesn&rsquo;t break.
              It doesn&rsquo;t override. It tracks the trajectory and reports
              exactly how close to collapse it is&mdash;and exactly how much
              a genuine intervention would matter.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>You override volatility.</em></p>
            <p className="calc-closing-final">We predict what happens BECAUSE of it.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default VolatilityIsSignal;
